import type { Page, SelectOptionDefault } from '../../types';

export const cpfClusters = {
  Synergy: ['DWP', 'DEFRA', 'MOJ', 'HO'],
  Matrix: ['DSIT', 'DESNZ', 'CO', 'DfE', 'HMT', 'DHSC', 'DCMS', 'AGO'],
  Defence: ['MOD Mil Pers'],
  Unity: ['HMRC', 'DfT', 'MHCLG'],
  Overseas: ['FCDO'],
};

const departmentNames: Record<string, string> = {
  DWP: 'Department for Work and Pensions',
  DEFRA: 'Department for Environment, Food and Rural Affairs',
  MOJ: 'Ministry of Justice',
  HO: 'Home Office',
  DSIT: 'Department for Science, Innovation and Technology',
  DESNZ: 'Department for Energy Security and Net Zero',
  CO: 'Cabinet Office',
  DfE: 'Department for Education',
  HMT: 'HM Treasury',
  DHSC: 'Department of Health and Social Care',
  DCMS: 'Department for Culture, Media and Sport',
  AGO: "Attorney General's Office",
  'MOD Mil Pers': 'Ministry of Defence Military Personnel',
  HMRC: 'HM Revenue and Customs',
  DfT: 'Department for Transport',
  MHCLG: 'Ministry of Housing, Communities and Local Government',
  FCDO: 'Foreign, Commonwealth and Development Office',
};

export const departmentOptions: SelectOptionDefault[] = Object.entries(cpfClusters)
  .flatMap(([cluster, departments]) =>
    departments.map((department) => ({
      option: `${departmentNames[department]} (${department})`,
      questions: [
        {
          questionType: 'textInput' as const,
          name: 'cluster',
          title: 'Cluster',
          defaultValue: cluster,
          readOnly: true,
          hint: 'Automatically populated from your department.',
          rules: { required: true },
        },
      ],
    })),
  )
  .sort((a, b) => a.option.localeCompare(b.option));

const coverSheet: Page[] = [
  {
    pageType: 'information',
    name: 'before-you-start',
    title: 'Common Performance Framework quarterly return',
    description: `Provide monthly figures for your department within the selected financial quarter.

Enter 0 only when the measured value is zero. If a figure is unavailable, leave it blank and explain which month is missing and why in the Notes column alongside that metric.

The monthly metrics page lets you download all metric tables in one Excel workbook and upload the completed workbook. Keep sheet names, metric labels and column headings unchanged. Uploading replaces the figures and notes in those tables.

Quarter 1: April, May, June. Quarter 2: July, August, September. Quarter 3: October, November, December. Quarter 4: January, February, March.

This first version includes all 74 metrics from the supplied consolidation workbook for review. Recruitment and Qlik figures must be entered manually until those data sources are connected.

Treat completed returns and downloaded workbooks as Official-Sensitive.`,
  },
  {
    pageType: 'question',
    name: 'return-details',
    title: 'Department details',
    questions: [
      {
        questionType: 'reportPeriod',
        name: 'report-period',
        title: 'Reporting period',
        defaultValue: 'auto-1',
        rules: { required: true },
      },
      {
        questionType: 'textInput',
        name: 'contact-email',
        title: 'Reporting lead email address',
        userEmail: true,
        hint: 'The submission confirmation is sent to the signed-in submitter.',
        rules: { required: true, email: true },
      },
      {
        questionType: 'textInput',
        name: 'contact-name',
        title: 'Reporting lead full name',
        userNameFromEmail: 'first.last',
        userNameFromEmailField: 'contact-email',
        hint: 'Populated from the reporting lead email when it contains firstname.surname. Check your name or enter it if blank.',
        rules: { required: true },
      },
      {
        questionType: 'select',
        name: 'department',
        title: 'Department',
        options: departmentOptions,
        hintOption: 'Select your department',
        rules: { required: true },
      },
    ],
  },
];

export default coverSheet;
